import React, { useMemo, useState } from 'react';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useScrapeSessionContext, useSurchargeGridsContext } from '../../PricingToolSessionContext';
import SectionLabel from '../../shared/SectionLabel';
import { computeStaySurchargeByDate } from '../surcharges/computeStaySurcharge';

import OtaRateShopForm from './OtaRateShopForm';
import RawCsvDataTable from './RawCsvDataTable';
import ScrapeCsvTable from './ScrapeCsvTable';
import ScrapePrvSnapshot from './ScrapePrvSnapshot';
import StayInputsForm from './StayInputsForm';
import { computeDailyDiscountStats } from './otaParity/computeGenuineDiscount';
import { computeDailyMarketStats } from './otaParity/computeOtaMarketStats';

export default function ScrapeTab() {
  const [rawDataOpen, setRawDataOpen] = useState(false);

  const {
    offerId,
    formKey,
    stayInputs,
    setStayInputs,
    rateShopInputs,
    setRateShopInputs,
    status,
    progressText,
    error,
    clearError,
    parsedCsv,
    submitScrape,
    loadMostRecentJob,
  } = useScrapeSessionContext();

  const isWorking = status === 'submitting' || status === 'scraping';

  const marketStats = useMemo(
    () => (parsedCsv ? computeDailyMarketStats(parsedCsv, undefined, stayInputs?.blackoutDates) : []),
    [parsedCsv, stayInputs?.blackoutDates],
  );

  const { session: surchargeSession } = useSurchargeGridsContext();

  const discountStats = useMemo(() => {
    if (!stayInputs) {
      return [];
    }

    const stayNightlySurchargeByDate = computeStaySurchargeByDate(
      marketStats.map((stats) => stats.date),
      stayInputs.nights,
      surchargeSession.grids,
    );

    return computeDailyDiscountStats(marketStats, { ...stayInputs, stayNightlySurchargeByDate });
  }, [marketStats, stayInputs, surchargeSession.grids]);

  return (
    <TabPanelShell
      title="Scrape"
      subtitle="Run a new OTA rate scrape or load the most recent one. Review the rate table before continuing to package options."
    >
      <Stack spacing={3}>
        <Box>
          <SectionLabel>Stay inputs</SectionLabel>
          {/* Remount on offer change or draft restore so defaultValues reset. */}
          <StayInputsForm key={`${offerId}-${formKey}`} initialValues={stayInputs} onChange={setStayInputs} />
        </Box>

        <Divider />

        <Box>
          <SectionLabel>OTA rate shop</SectionLabel>
          <OtaRateShopForm
            key={`${offerId}-${formKey}`}
            initialValues={rateShopInputs}
            onChange={setRateShopInputs}
            stayInputs={stayInputs}
            isWorking={isWorking}
            progressText={progressText}
            error={error}
            clearError={clearError}
            submitScrape={submitScrape}
            loadMostRecentJob={loadMostRecentJob}
          />

          {parsedCsv && (
            <Button variant="text" sx={{ mt: 2 }} onClick={() => setRawDataOpen(true)}>
              Open full CSV view
            </Button>
          )}

          {parsedCsv && stayInputs && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                {parsedCsv.rows.length} rows loaded
              </Typography>
              <ScrapeCsvTable
                parsed={parsedCsv}
                marketStats={marketStats}
                discountStats={discountStats}
                currencyCode={stayInputs.currencyCode}
                maxHeight={780}
              />
            </Box>
          )}
        </Box>

        {parsedCsv && stayInputs && (
          <>
            <Divider />
            <ScrapePrvSnapshot parsed={parsedCsv} marketStats={marketStats} discountStats={discountStats} />
          </>
        )}

        {parsedCsv && stayInputs && (
          <RawCsvDataTable
            open={rawDataOpen}
            onClose={() => setRawDataOpen(false)}
            parsed={parsedCsv}
            marketStats={marketStats}
            discountStats={discountStats}
            currencyCode={stayInputs.currencyCode}
          />
        )}
      </Stack>
    </TabPanelShell>
  );
}
